import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/toggle-group'
import {
  AdditionalAction,
  DataTable
} from '@frontend/components/enhanced/data-table'
import { AuthenticatedUser, RawUserData } from '@frontend/core'
import {
  useCreateUserMutation,
  useUnlinkUserFromOrgMutation,
  useUpdateUserMutation,
  useUpdateUserOrgRoleMutation
} from '@frontend/core/lib/domains/users/api'
import {
  useResetUserPasswordMutation,
  useWhoAmIQuery
} from '@frontend/core/lib/modules/authentication'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { addNotification } from '@frontend/core/lib/modules/notifications'
import { Avatar, AvatarFallback, toast } from '@frontend/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { ColumnDef } from '@tanstack/react-table'
import { Ban, CheckCircle2Icon, Link2, LoaderIcon, Unlink } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { StaticAssetImage } from '../../modules/images/components'
import { useAppDispatch } from '../../store/hooks'
import { SuperAdminOnly } from '../components'
import { useIsSuperAdmin } from '../hooks'
import { useSelectedOrganisation } from '../organisations'
import { LinkUserModal } from './components/link-user-modal'
import { UnlinkUserModal } from './components/unlink-user-modal'
import { useUsersData } from './hooks'

const formSchema = z.object({
  id: z.string().optional(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  orgRole: z.enum(['Admin', 'Viewer', 'NoAccess']),
  globalRole: z.enum(['SportableSuperAdmin', 'Viewer', 'NoAccess']).optional(),
  company: z.string().optional(),
  description: z.string().optional(),
  phoneNumber: z.string().optional(),
  phoneNumberCountryCode: z.string().optional()
})

export function UserForm({
  onSubmit,
  initialValues
}: {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  initialValues?: z.infer<typeof formSchema>
}) {
  const { organisation } = useSelectedOrganisation()
  const isSuperAdmin = useIsSuperAdmin()
  const [updateUser, updateResult] = useUpdateUserMutation()
  const [updateUserOrgRole] = useUpdateUserOrgRoleMutation()
  const [createUser, createResult] = useCreateUserMutation()
  const isEditing = !!initialValues?.id

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!organisation) return
    try {
      if (values.id) {
        const profilePayload: Parameters<typeof updateUser>[0] = {
          id: values.id,
          firstName: values.firstName,
          lastName: values.lastName,
          company: values.company,
          description: values.description,
          phoneNumber: values.phoneNumber,
          phoneNumberCountryCode: values.phoneNumberCountryCode,
          ...(isSuperAdmin && values.globalRole
            ? { globalRole: values.globalRole }
            : {})
        }

        toast.promise(
          Promise.all([
            updateUser(profilePayload).unwrap(),
            updateUserOrgRole({
              organisationId: organisation?.id,
              userId: values.id,
              role: values.orgRole
            }).unwrap()
          ]),
          {
            loading: 'Updating...',
            success: ([userData]) => {
              onSubmit(values)
              return `${userData.firstName} ${userData.lastName} has been updated`
            },
            error: (error) => {
              console.error('Update failed:', error)
              return 'Error'
            }
          }
        )
      } else {
        toast.promise(
          createUser({
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            orgRole: values.orgRole,
            globalRole: values.globalRole,
            organisationId: organisation?.id,
            company: values.company,
            description: values.description,
            phoneNumber: values.phoneNumber,
            phoneNumberCountryCode: values.phoneNumberCountryCode
          }).unwrap(),
          {
            loading: 'Creating...',
            success: (data: RawUserData) => {
              onSubmit(values)
              return `${data.firstName} ${data.lastName} has been created`
            },
            error: 'Error'
          }
        )
      }
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      orgRole: 'Viewer',
      globalRole: 'Viewer',
      company: '',
      description: '',
      phoneNumber: '',
      phoneNumberCountryCode: ''
    },
    values: initialValues
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
        <div className='space-y-2'>
          <Input
            id='user-form-organisation'
            data-cy='user-form-organisation'
            name='organisation'
            label='Organisation'
            value={organisation?.name || ''}
            disabled={true}
            className='bg-muted'
          />
        </div>
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input
                  type='email'
                  placeholder='email@example.com'
                  disabled={isEditing}
                  className={isEditing ? 'bg-muted' : ''}
                  data-cy='user-form-email'
                  aria-required={true}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex gap-4'>
          <FormField
            control={form.control}
            name='firstName'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder=''
                    data-cy='user-form-first-name'
                    aria-required={true}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='lastName'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder=''
                    data-cy='user-form-last-name'
                    aria-required={true}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name='orgRole'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organisation Role</FormLabel>
              <FormControl>
                <ToggleGroup
                  type='single'
                  data-cy='user-form-org-role'
                  aria-required={true}
                  {...field}
                  onValueChange={(val) => field.onChange(val)}
                >
                  {formSchema.shape.orgRole?.options?.map((option) => (
                    <ToggleGroupItem
                      key={option}
                      value={option}
                      className='data-[state=on]:bg-primary data-[state=on]:text-primary-foreground min-w-auto border'
                    >
                      {option}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {isSuperAdmin && (
          <FormField
            control={form.control}
            name='globalRole'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Global Role</FormLabel>
                <FormControl>
                  <ToggleGroup
                    type='single'
                    data-cy='user-form-global-role'
                    {...field}
                    value={field.value ?? ''}
                    onValueChange={(val) => field.onChange(val)}
                  >
                    {formSchema.shape.globalRole
                      .unwrap()
                      .options?.map((option) => (
                        <ToggleGroupItem
                          key={option}
                          value={option}
                          className='data-[state=on]:bg-primary data-[state=on]:text-primary-foreground min-w-auto border'
                        >
                          {option}
                        </ToggleGroupItem>
                      ))}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <div className='flex gap-4'>
          <FormField
            control={form.control}
            name='company'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <FormLabel>Company</FormLabel>
                <FormControl>
                  <Input
                    placeholder=''
                    data-cy='user-form-company'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='description'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input
                    placeholder=''
                    data-cy='user-form-description'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className='flex gap-4'>
          <FormField
            control={form.control}
            name='phoneNumberCountryCode'
            render={({ field }) => (
              <FormItem className='w-32'>
                <FormLabel>Country Code</FormLabel>
                <FormControl>
                  <Input
                    placeholder='+1'
                    data-cy='user-form-country-code'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='phoneNumber'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder=''
                    data-cy='user-form-phone-number'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className='flex justify-end'>
          <Button type='submit' data-cy='user-form-submit'>
            {' '}
            {form.getValues().id
              ? updateResult.isLoading
                ? 'Updating...'
                : 'Update'
              : createResult.isLoading
                ? 'Creating...'
                : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export function UserTable({
  filteredData,
  isLoading,
  filters,
  updateFilterValue,
  showFilters = []
}: ReturnType<typeof useUsersData> & {
  showFilters?: (keyof ReturnType<typeof useUsersData>['filters'])[]
}) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isSuperAdmin = useIsSuperAdmin()
  const { data: user } = useWhoAmIQuery()

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false)

  const { organisation: orgData } = useSelectedOrganisation()
  const [unlinkUserFromOrg] = useUnlinkUserFromOrgMutation()
  const [resetUserPassword] = useResetUserPasswordMutation()

  const columns: ColumnDef<RawUserData>[] = useMemo(
    () => [
      {
        id: 'img',
        header: '',
        cell: ({ row }) => {
          const user = row.original
          return (
            <div className='flex items-center justify-center gap-2'>
              {user.image && (
                <StaticAssetImage
                  src={user.image}
                  alt={user.firstName + ' ' + user.lastName}
                  preset={IMAGE_PRESETS.playerThumbnail}
                  className='h-8 w-8 rounded-full object-cover object-top'
                />
              )}
            </div>
          )
        },
        enableSorting: false
      },
      {
        id: 'name',
        accessorFn: (row) => row.firstName + ' ' + row.lastName,
        header: 'Name',
        cell: ({ row }) => {
          const user = row.original
          return (
            <div className='flex items-center gap-2'>
              <Button
                variant='link'
                className='text-foreground w-fit px-0 text-left'
                onClick={() => navigate(`/users/${row.original.id}`)}
              >
                {user.firstName} {user.lastName}
              </Button>
            </div>
          )
        },
        enableSorting: true,
        enableHiding: true
      },
      {
        id: 'org_role',
        accessorKey: 'orgRole',
        header: 'Organisation Role',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='w-32'>
            <Badge variant='outline' className='text-muted-foreground px-1.5'>
              {row.original.orgRole}
            </Badge>
          </div>
        )
      },
      {
        id: 'global_role',
        accessorKey: 'globalRole',
        header: 'Global Role',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='w-32'>
            <Badge variant='outline' className='text-muted-foreground px-1.5'>
              {row.original.globalRole}
            </Badge>
          </div>
        )
      },
      {
        id: 'role',
        accessorKey: 'userType',
        header: 'User type',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='w-32'>
            <Badge variant='outline' className='text-muted-foreground px-1.5'>
              {row.original.userType}
            </Badge>
          </div>
        )
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => (
          <Badge
            variant='outline'
            className='text-muted-foreground flex gap-1 px-1.5 [&_svg]:size-3'
          >
            {row.original.status === 'Active' && (
              <CheckCircle2Icon className='text-green-500 dark:text-green-400' />
            )}
            {row.original.status === 'WaitingEmailVerification' && (
              <LoaderIcon className='animate-spin text-yellow-500 dark:text-yellow-400' />
            )}
            {row.original.status === 'Disabled' && (
              <Ban className='text-red-500 dark:text-red-400' />
            )}
            {row.original.status}
          </Badge>
        )
      }
    ],
    [navigate]
  )

  const actions: AdditionalAction<RawUserData>[] = useMemo(() => {
    const actions: AdditionalAction<RawUserData>[] = []

    const handleUnlinkConfirm = async (user: RawUserData) => {
      if (!orgData) return
      dispatch(
        addNotification({
          displayType: 'modal',
          title: 'Remove user from organisation?',
          message: `User ${user.firstName} ${user.lastName} will be removed from ${orgData.name}`,
          type: 'warning',
          action: {
            label: 'Unlink',
            onClick: async () => {
              try {
                await unlinkUserFromOrg({
                  organisationId: orgData.id,
                  userId: user.id
                }).unwrap()
                toast.success('User unlinked successfully')
              } catch {
                toast.error('Failed to unlink user')
              }
            }
          }
        })
      )
    }

    if (isSuperAdmin) {
      actions.push({
        label: 'Unlink',
        onClick: (row: RawUserData) => handleUnlinkConfirm(row),
        className: 'text-superadmin'
      })
    }

    actions.push({
      label: 'Reset Password',
      onClick: async (row: RawUserData) => {
        await resetUserPassword(row.id).unwrap()
      }
    })

    return actions
  }, [dispatch, orgData, unlinkUserFromOrg, resetUserPassword, isSuperAdmin])

  const toolbarActions = useMemo(() => {
    if (!isSuperAdmin) return []

    return [
      {
        label: 'Link User',
        icon: <Link2 className='h-4 w-4' />,
        onClick: () => setIsLinkModalOpen(true),
        variant: 'superAdmin' as const,
        hideOnMobile: true
      },
      {
        label: 'Unlink User',
        icon: <Unlink className='h-4 w-4' />,
        onClick: () => setIsUnlinkModalOpen(true),
        variant: 'superAdmin' as const,
        hideOnMobile: true
      }
    ]
  }, [isSuperAdmin])

  return (
    <>
      <DataTable
        name='User'
        columns={columns}
        data={filteredData}
        dataForm={UserForm}
        editDescription='Update user profile and role settings.'
        createDescription='Add a new user to the organisation.'
        isLoading={isLoading}
        additionalActions={actions}
        toolbarActions={toolbarActions}
        filters={filters}
        updateFilterValue={updateFilterValue}
        showFilters={showFilters}
        defaultSorting={[{ id: 'name', desc: false }]}
        enableCreate={user?.globalRole === 'SportableSuperAdmin'}
      />

      <SuperAdminOnly>
        <LinkUserModal
          isOpen={isLinkModalOpen}
          onOpenChange={setIsLinkModalOpen}
        />

        <UnlinkUserModal
          isOpen={isUnlinkModalOpen}
          onOpenChange={setIsUnlinkModalOpen}
        />
      </SuperAdminOnly>
    </>
  )
}

export function AvatarProfile({ user }: { user: AuthenticatedUser }) {
  return (
    <Avatar className='h-20 w-20'>
      <StaticAssetImage
        src={user?.image}
        alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
        preset={IMAGE_PRESETS.playerLarge}
        className='h-20 w-20 rounded-full'
        fallback={
          <AvatarFallback className='h-20 w-20 text-lg'>
            {user?.firstName?.[0] || ''}
            {user?.lastName?.[0] || ''}
          </AvatarFallback>
        }
      />
    </Avatar>
  )
}
